/* ============================================================
   WildKind Motion — wk-motion.js
   Vanilla JS, no dependencies. Loaded with `defer`.
   Adds .wk-motion-ready to <html> only when it can actually
   run, so the CSS pre-states never hide content if this file
   fails to load or IntersectionObserver is unavailable.
   ============================================================ */
(function () {
  'use strict';

  var REVEAL_MS = 650;
  var reduced = false;
  try {
    reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (err) {
    reduced = false;
  }

  var CARD_SELECTOR = [
    '.wk-deals__cell',
    '.wk-rescards__cell',
    '.wk-ways__tile',
    '.wk-purpose__stat',
    '.wk-trust-strip__item',
    '.wk-catindex__item',
    '.wk-catgrid__cell',
    'product-card',
    '.product-card',
    '.product-grid__item',
    '.card-wrapper',
    '.collection-card',
    '.resource-list__slide',
    '.blog-post-card'
  ].join(', ');

  var HOVER_CARD_SELECTOR =
    'product-card, .product-card, .product-grid__item, .card-wrapper, .resource-list__slide, .blog-post-card';

  var HEADER_SELECTOR =
    '.wk-deals__header, .wk-rescards__header, .wk-ways__header, .wk-ways__heading, .wk-purpose__left, .wk-catindex__header, .wk-catgrid__header';

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  onReady(function () {
    try {
      initScrollProgress();
      if (reduced || !('IntersectionObserver' in window)) return;

      document.documentElement.classList.add('wk-motion-ready');

      var main = document.getElementById('MainContent');
      if (!main) return;

      var sections = [];
      for (var i = 0; i < main.children.length; i++) {
        var child = main.children[i];
        if (child.classList && child.classList.contains('shopify-section')) {
          sections.push(child);
        }
      }
      var hero = sections.length ? sections[0] : null;

      if (main.getAttribute('data-template') === 'index') {
        initHero(hero);
      }
      initReveals(sections, hero);
      initCounters();
    } catch (err) {
      /* Never leave content hidden if anything goes wrong. */
      document.documentElement.classList.remove('wk-motion-ready');
    }
  });

  /* ---------- Scroll progress hairline ---------- */
  function initScrollProgress() {
    var bar = document.createElement('div');
    bar.className = 'wk-scroll-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    var ticking = false;
    function update() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      var pct = max > 0 ? (window.scrollY / max) * 100 : 0;
      bar.style.width = Math.min(100, Math.max(0, pct)) + '%';
      ticking = false;
    }
    function requestUpdate() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  }

  /* ---------- Hero: Ken Burns + word-by-word reveal ---------- */
  function initHero(hero) {
    if (!hero) return;

    if (hero.querySelector('slideshow-component, slideshow-container, slideshow-slide, .slideshow')) {
      var img = hero.querySelector('slideshow-slide img') || hero.querySelector('img');
      if (img) img.classList.add('wk-kenburns');
    }

    var candidates = hero.querySelectorAll('h1, h2, p');
    var heading = null;
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.children.length > 0) continue;
      var txt = (el.textContent || '').trim();
      if (txt.length < 4 || txt.length > 160) continue;
      var size = parseFloat(window.getComputedStyle(el).fontSize) || 0;
      if (size >= 28) {
        heading = el;
        break;
      }
    }
    if (!heading) return;

    heading.classList.add('wk-hero-reveal', 'wk-hero-title');
    var words = (heading.textContent || '').trim().split(/\s+/);
    heading.textContent = '';
    for (var w = 0; w < words.length; w++) {
      var span = document.createElement('span');
      span.className = 'wk-word';
      span.textContent = words[w];
      span.style.transitionDelay = w * 80 + 'ms';
      heading.appendChild(span);
      if (w < words.length - 1) {
        heading.appendChild(document.createTextNode(' '));
      }
    }
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        heading.classList.add('wk-in');
      });
    });
  }

  /* ---------- Scroll reveals with sibling stagger ---------- */
  function initReveals(sections, hero) {
    var targets = [];
    for (var i = 0; i < sections.length; i++) {
      var sec = sections[i];
      if (sec === hero) continue;
      if (sec.querySelector('.wk-marquee')) continue; /* marquee animates itself */
      var cards = sec.querySelectorAll(CARD_SELECTOR);
      if (cards.length) {
        tagGroup(cards, true, targets);
        tagGroup(sec.querySelectorAll(HEADER_SELECTOR), false, targets);
      } else {
        var kids = [];
        for (var k = 0; k < sec.children.length; k++) kids.push(sec.children[k]);
        tagGroup(kids, false, targets);
      }
    }
    if (!targets.length) return;

    var vh = window.innerHeight || 800;
    var normal = [];
    var tall = [];
    for (var t = 0; t < targets.length; t++) {
      if (targets[t].getBoundingClientRect().height > vh * 0.7) {
        tall.push(targets[t]);
      } else {
        normal.push(targets[t]);
      }
    }
    makeObserver(normal, 0.15);
    makeObserver(tall, 0.05); /* very tall blocks would never hit 15%% */
  }

  function tagGroup(list, isCard, targets) {
    var perParent = new Map();
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.hasAttribute('data-wk-reveal')) continue;
      if (el.parentElement && el.parentElement.closest('[data-wk-reveal]')) continue;
      var parent = el.parentElement || document.body;
      var idx = perParent.get(parent) || 0;
      perParent.set(parent, idx + 1);
      var delay = Math.min(idx * 70, 420);
      el.setAttribute('data-wk-reveal', '');
      el.setAttribute('data-wk-delay', String(delay));
      if (delay) el.style.transitionDelay = delay + 'ms';
      if (isCard) {
        el.classList.add('wk-card-pre');
        if (el.matches && el.matches(HOVER_CARD_SELECTOR)) {
          el.classList.add('wk-card');
        }
      }
      targets.push(el);
    }
  }

  function makeObserver(els, threshold) {
    if (!els.length) return;
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          reveal(entries[i].target);
          io.unobserve(entries[i].target);
        }
      },
      { threshold: threshold, rootMargin: '0px 0px -4% 0px' }
    );
    for (var i = 0; i < els.length; i++) io.observe(els[i]);
  }

  function reveal(el) {
    el.classList.add('wk-in');
    var delay = parseInt(el.getAttribute('data-wk-delay') || '0', 10);
    window.setTimeout(function () {
      /* Cleanup: hand transitions back to hover micro-interactions. */
      el.removeAttribute('data-wk-reveal');
      el.removeAttribute('data-wk-delay');
      el.style.transitionDelay = '';
    }, REVEAL_MS + delay + 60);
  }

  /* ---------- Animated counters ---------- */
  function initCounters() {
    var els = document.querySelectorAll('[data-wk-count]');
    if (!els.length) return;
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          animateCount(entries[i].target);
          io.unobserve(entries[i].target);
        }
      },
      { threshold: 0.6 }
    );
    for (var i = 0; i < els.length; i++) io.observe(els[i]);
  }

  function animateCount(el) {
    var original = (el.textContent || '').trim();
    var m = original.match(/^([^0-9]*)([0-9][0-9.,]*)(.*)$/);
    if (!m) return;
    var target = parseFloat(m[2].replace(/,/g, ''));
    if (isNaN(target)) return;
    var prefix = m[1];
    var suffix = m[3];
    var decimals = (m[2].split('.')[1] || '').length;
    var duration = 1200;
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / duration);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + (target * eased).toFixed(decimals) + suffix;
      if (p < 1) {
        window.requestAnimationFrame(frame);
      } else {
        el.textContent = original;
      }
    }
    window.requestAnimationFrame(frame);
  }
})();
